/**
 * Keeps every window of ONE session on the same tokens.
 *
 * The meeting call opens in its own popup window (pages/meetings), and a
 * second tab or a reopened popup is common. Each window has its own copy of
 * the auth store, so when one of them refreshed and the server rotated the
 * refresh token, the others kept presenting the old one. The server now
 * tolerates that for a few minutes (api lib/refresh-rotation.ts), but the
 * right fix is for the windows to agree: whoever rotates tells the others,
 * and a sign-out in one window signs the others out too (the QA catalogue's
 * "logout in one tab signs out other open tabs" case).
 *
 * `BroadcastChannel` is same-origin only, so nothing leaves the app's own
 * windows, which already share `localStorage`. Where it is missing (very old
 * WebViews, some test environments) the sync is simply off.
 */
import { useAuthStore, type AuthUser } from "@/store/auth-store";

export const SESSION_SYNC_CHANNEL = "campushubai-session";

export type SessionSyncMessage =
  | {
      kind: "session";
      userId: string;
      token: string;
      refreshToken: string | null;
      /** Sent when the signed-in person changed too (sign-in, role switch). */
      user?: AuthUser;
      activeSchoolId?: string | null;
    }
  | { kind: "logout" };

export type SessionSyncChannel = {
  postMessage: (msg: SessionSyncMessage) => void;
  close: () => void;
  onmessage: ((ev: { data: unknown }) => void) | null;
};

let channel: SessionSyncChannel | null = null;
let opened = false;
/** True while a message from another window is being applied — do not echo it back. */
let applyingRemote = false;

function openChannel(): SessionSyncChannel | null {
  if (opened) return channel;
  opened = true;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(SESSION_SYNC_CHANNEL) as unknown as SessionSyncChannel;
  } catch {
    channel = null;
  }
  return channel;
}

/** Exported for tests — swap the channel for a fake. */
export function __setSessionSyncChannel(fake: SessionSyncChannel | null): void {
  channel = fake;
  opened = true;
}

function post(msg: SessionSyncMessage): void {
  if (applyingRemote) return;
  const ch = openChannel();
  if (!ch) return;
  try {
    ch.postMessage(msg);
  } catch {
    // A closed channel or a serialisation error must never break sign-in.
  }
}

export function isSessionSyncMessage(value: unknown): value is SessionSyncMessage {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (v["kind"] === "logout") return true;
  return (
    v["kind"] === "session"
    && typeof v["userId"] === "string"
    && typeof v["token"] === "string"
    && (v["refreshToken"] === null || typeof v["refreshToken"] === "string")
  );
}

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

/**
 * What one store change means for the other windows. Pure, exported for
 * tests: null = nothing to tell them.
 */
export function sessionChangeMessage(next: AuthSnapshot, prev: AuthSnapshot): SessionSyncMessage | null {
  if (prev.isAuthenticated && !next.isAuthenticated) return { kind: "logout" };
  if (!next.isAuthenticated || !next.user || !next.token) return null;
  const tokensChanged = next.token !== prev.token || next.refreshToken !== prev.refreshToken;
  const personChanged = next.user !== prev.user;
  if (!tokensChanged && !personChanged) return null;
  return {
    kind: "session",
    userId: next.user.id,
    token: next.token,
    refreshToken: next.refreshToken,
    ...(personChanged ? { user: next.user, activeSchoolId: next.activeSchoolId } : {}),
  };
}

/**
 * Apply a message from another window to THIS window's store. Returns what it
 * did, so the caller (and the tests) can act on it.
 */
export function applySessionSyncMessage(msg: SessionSyncMessage): "adopted" | "signed-out" | "ignored" {
  const state = useAuthStore.getState();
  applyingRemote = true;
  try {
    if (msg.kind === "logout") {
      if (!state.isAuthenticated) return "ignored";
      void state.logout().catch(() => undefined);
      return "signed-out";
    }
    if (!state.isAuthenticated) return "ignored";
    // A different person signed in over there (shared PC, role switch to
    // another account): adopt only when the message carries who they are.
    if (state.user?.id !== msg.userId && !msg.user) return "ignored";
    if (!msg.user && state.token === msg.token && state.refreshToken === msg.refreshToken) return "ignored";
    useAuthStore.setState({
      token: msg.token,
      refreshToken: msg.refreshToken,
      ...(msg.user ? { user: msg.user } : {}),
      ...(msg.user && msg.activeSchoolId !== undefined ? { activeSchoolId: msg.activeSchoolId } : {}),
    });
    return "adopted";
  } finally {
    applyingRemote = false;
  }
}

/**
 * Start publishing this window's session changes and listening for the other
 * windows'. Returns a stop function. On a remote sign-out the window
 * hard-navigates to /login (same as signOut()) so a frozen authenticated page
 * can never linger.
 */
export function installSessionSync(
  navigate: (path: string) => void = (p) => window.location.replace(p),
): () => void {
  const ch = openChannel();
  if (!ch) return () => undefined;
  const unsubscribe = useAuthStore.subscribe((next, prev) => {
    const msg = sessionChangeMessage(next, prev);
    if (msg) post(msg);
  });
  ch.onmessage = (ev) => {
    if (!isSessionSyncMessage(ev.data)) return;
    const outcome = applySessionSyncMessage(ev.data);
    if (outcome === "signed-out") navigate("/login");
  };
  return () => {
    unsubscribe();
    ch.onmessage = null;
  };
}
